import { api } from '../api/client';
import { token as liveToken } from '../auth/session';
import { translate } from '../i18n/i18n';

export type ReportLine = {
  programId: string;
  programName: string;
  track: 'CURRENT' | 'MAINTENANCE';
  /** Right first time, as a whole percentage of that program's tries in the period. */
  percent: number;
  correct: number;
  trials: number;
  /** Null when no unprompted check was taken in the period. */
  probePercent: number | null;
  probeTrials: number;
  averageMs: number | null;
  helped: number;
  rewards: number;
};

export type Report = {
  learnerId: string;
  learnerName: string;
  from: string;
  to: string;
  percent: number;
  correct: number;
  trials: number;
  rewards: number;
  programsPractised: number;
  programsOnMaintenance: number;
  lines: ReportLine[];
};

function query(learnerId: string | null, from: string, to: string): string {
  const parts = [`from=${from}`, `to=${to}`];
  if (learnerId) parts.unshift(`learnerId=${encodeURIComponent(learnerId)}`);
  return parts.join('&');
}

export async function fetchReport(
  learnerId: string | null,
  from: string,
  to: string,
): Promise<Report> {
  return api<Report>(`/reports?${query(learnerId, from, to)}`, { token: true });
}

/**
 * Hand the browser the file.
 *
 * <p>FETCHED RATHER THAN LINKED. The endpoint wants the bearer token, and a plain link carries
 * none — it would simply come back unauthorised, which on this client also looks like a dead
 * session and signs the reader out.
 */
export async function downloadReportCsv(
  learnerId: string | null,
  from: string,
  to: string,
): Promise<void> {
  const t = await liveToken();
  const res = await fetch(`/api/reports/csv?${query(learnerId, from, to)}`, {
    headers: t ? { Authorization: `Bearer ${t}` } : {},
    credentials: 'omit',
  });
  if (!res.ok) throw new Error(translate('That file could not be made just now.'));

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filenameFrom(res.headers.get('Content-Disposition')) ?? 'report.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function filenameFrom(header: string | null): string | null {
  if (!header) return null;
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match ? match[1] : null;
}

/** ISO date, as the date inputs and the API both want it. */
export function isoDay(d: Date): string {
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString().slice(0, 10);
}

export function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoDay(d);
}
